/**
 * Functions for creating SVGs and SVG elements.
 */

import { createElement, createElementNS } from "../../util/crossplatform";
import { generate } from "../../util/guid";


export const svgElem = (type: string): SVGElement => {
    return createElementNS("http://www.w3.org/2000/svg", type);
};

export const svgRect = (height: number, width: number): SVGElement => {
    const el = svgElem("rect");
    el.setAttribute("height", `${height}`);
    el.setAttribute("width", `${width}`);
    return el;
};

export const svgRoot = (): SVGElement => {
    const el = svgElem("svg");
    el.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    el.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    el.setAttribute("version", "1.1");
    return el;
};

export const svgUse = (link: string): SVGElement => {
    const el = svgElem("use");
    el.setAttribute("xlink:href", link); // For environments where plain href is not supported.
    el.setAttribute("href", link); // For environments where xlink:href is deprecated.
    return el;
};

// Create SVG with height and width of supplied SVG string.
export function initSvgFromBackground(img: string): [SVGElement | null, number, number] {
    const svg = svgRoot();

    // Get dimensions
    const matches = /viewBox="0 0 (.+?) (.+?)"/.exec(img) || ["0", "0"];
    const bgWidth = parseFloat(matches[1]);
    const bgHeight = parseFloat(matches[2]);

    svg.setAttribute("height", `${bgHeight}`);
    svg.setAttribute("width", `${bgWidth}`);
    return [svg, bgHeight, bgWidth];
}

// Create SVG with supplied height and width.
export function initSvgFromDimensions(bgHeight: number, bgWidth: number): SVGElement {
    const svg = svgRoot();
    svg.setAttribute("height", `${bgHeight}`);
    svg.setAttribute("width", `${bgWidth}`);
    return svg;
}

// Add a definition by setting innerHTML to defText.
export const addDef = (defs: SVGElement, defText: string, id: string) => {
    const elem = svgElem("g");
    defs.appendChild(elem);
    elem.setAttribute("id", id);
    elem.innerHTML = defText;
};

// Add background image to SVG.
export function addBackground(svg: SVGElement, defs: SVGElement, backgroundImg: string) {
    const uuid = generate();
    addDef(defs, backgroundImg, `bgdef-${uuid}`);
    svg.appendChild(svgUse(`#bgdef-${uuid}`));
}

// Definition for drop shadow.
export const dropShadow = (dx: number, dy: number, blur: number, id: string): HTMLElement => {
    const shadow = createElement("filter");
    shadow.setAttribute("id", id);
    shadow.setAttribute("height", "200%");
    shadow.setAttribute("width", "200%");
    shadow.setAttribute("x", "-50%");
    shadow.setAttribute("y", "-50%");

    const feOffset = createElement("feOffset");
    shadow.appendChild(feOffset);
    feOffset.setAttribute("result", "offOut");
    feOffset.setAttribute("in", "SourceAlpha");
    feOffset.setAttribute("dx", `${dx}`);
    feOffset.setAttribute("dy", `${dy}`);

    const feGaussianBlur = createElement("feGaussianBlur");
    shadow.appendChild(feGaussianBlur);
    feGaussianBlur.setAttribute("result", "blurOut");
    feGaussianBlur.setAttribute("in", "offOut");
    feGaussianBlur.setAttribute("stdDeviation", `${blur}`);

    const feBlend = createElement("feBlend");
    shadow.appendChild(feBlend);
    feBlend.setAttribute("in", "SourceGraphic");
    feBlend.setAttribute("in2", "blurOut");
    feBlend.setAttribute("mode", "normal");

    return shadow;
};
